/**
 * Labstep
 *
 * @module screens/OrderRequest/Index
 * @desc OrderRequest Index
 */

import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { OrderRequestActionCreate } from 'labstep-web/components/OrderRequest/Action/Create';
import { OrderRequestEmptyState } from 'labstep-web/components/OrderRequest/EmptyState';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridNewIndex } from 'labstep-web/grid/NewIndex';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { OrderRequest } from 'labstep-web/models/order-request.model';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { OrderRequestIndexBreadcrumb } from 'labstep-web/screens/OrderRequest/Index/Breadcrumb';
import { OrderRequestIndexLeftPane } from 'labstep-web/screens/OrderRequest/Index/LeftPane';
import { LocationService } from 'labstep-web/services/location.service';
import { PostFilterComparison } from 'labstep-web/services/postFilter/constants';
import { PostFilterTag } from 'labstep-web/services/postFilter/filters/tag';
import type React from 'react';
import { useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { bulkActions, filters, sortOptions } from './constants';
import type { ScreensOrderRequestIndexMasterProps } from './types';

export const ScreensOrderRequestIndexMaster: React.FC<
  ScreensOrderRequestIndexMasterProps
> = ({ entityView, group, links, location }) => {
  const tagId = LocationService.getQueryParameter(location, 'tag_id');

  const breadcrumb = useMemo(
    () => <OrderRequestIndexBreadcrumb />,
    [],
  );
  const leftPane = useMemo(
    () => <OrderRequestIndexLeftPane links={links} />,
    [links],
  );
  const noResultsMessage = useMemo(
    () => <OrderRequestEmptyState />,
    [],
  );
  const action = useMemo(() => <OrderRequestActionCreate />, []);

  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    group_id: group.id,
  };

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'order_request_index',
    globalParams,
    entityView,
    loadedEntityView: new EntityView({
      ...entityView,
    }),
  };
  if (tagId) {
    paramsStateContext.initialSearchParams =
      GridFilterService.injectUserFilter(
        PostFilterTag.getNode([tagId, PostFilterComparison.eq]),
        entityView.parameters || {},
      );
  }

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <MasterIndex
          entityName={OrderRequest.entityName}
          breadcrumb={breadcrumb}
          leftPane={leftPane}
          filters={filters}
          noResultsMessage={noResultsMessage}
          action={action}
          bulkActions={bulkActions}
          sortOptions={sortOptions}
          useAllPages
          usePostFilter
          tableFormat={false}
        >
          {({
            entities,
            readNextPage,
            status,
          }: EntitySearchContainerChildrenProps) => (
            <GridNewIndex
              entityName={OrderRequest.entityName}
              group={group}
              entities={entities}
              entityView={entityView}
              status={status}
              loadMore={readNextPage}
            />
          )}
        </MasterIndex>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};

export default withRouter(ScreensOrderRequestIndexMaster);
